import { reactQueryClient } from '@/utils/reactQueryClient';
import { ENTITLEMENT, type UserEntitlementView } from '@heidi/workspace/contracts';
import { GET_ENTITLEMENTS, useIsEntitlementEnabled } from '@heidi/workspace/data';

/**
 * UKI-218: Shadow Heidi research trial mode (Greater Glasgow & Clyde).
 * Gated by the `shadow_heidi` access entitlement. When on, the app runs in
 * full shadow mode for clinical research:
 * - Note streaming and transcript display are hidden (backend still generates them).
 * - User types their own note in the Documents tab.
 * - Patient details + session metadata are required before recording.
 * - Clinician research profile prompted on first use.
 * - "Heidi X GGC Shadow Trial" branding shown in the header.
 */
export const useIsShadowHeidiEnabled = (): boolean => {
  const { isEnabled } = useIsEntitlementEnabled(ENTITLEMENT.SHADOW_HEIDI);
  return isEnabled;
};

/**
 * Non-hook variant for use outside of React (e.g. Redux models). Reads the
 * cached entitlements map populated by `useQueryEntitlements`.
 */
export const isShadowHeidiEnabled = (): boolean => {
  const entries = reactQueryClient.getQueriesData<Map<string, UserEntitlementView>>({
    queryKey: [GET_ENTITLEMENTS],
  });
  for (const [, data] of entries) {
    // A localStorage-persisted restore hydrates this entry as a plain object
    // (a Map does not survive JSON serialization), and the prefix match above
    // also sees stale-keyed restores that no active hook ever refetches. An
    // unguarded .get() on such an entry threw on every recording stop
    // (STOP_ERROR_V2 "t.get is not a function"), blocking the post-stop
    // template picker.
    if (data instanceof Map && data.get(ENTITLEMENT.SHADOW_HEIDI)?.enabled) return true;
  }
  return false;
};
